import { VatReport } from 'components/Vat/Report';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Spin } from 'antd';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import routes from 'routes';
import {
  addNoteRequest,
  awaitingApprovalRequest,
  cancelReturnRequest,
  changeStatusAfterUpdate,
  clientApproveRequest,
  exceptionsActionRequest,
  fetchActivityLogsRequest,
  fetchRunDataRequest,
  fetchRunDataSuccess,
  fetchVatLogsRequest,
  fetchViewRunDataRequest,
  generateReportRequest,
  internallyApproveRequest,
  loadReportRequest,
  loadReportTotalsRequest,
  lockReturnRequest,
  manualJournalPostRequest,
  notApproveRequest,
  resetVatData,
  saveReturnRequest,
  sendClientApprovalRequest,
  setUpdateFromPageRequest,
  submitHmrcReturnRequest,
  submitReturnRequest,
  updateActivityRoleRequest,
  cancelVatSaga,
  touchReport,
  refreshReportRequest,
} from 'store/vat/actions';
import {
  JournalPostData,
  SaveReturnRequestData,
  VatGenerateRequestData,
  VatReportData,
  VatRunData,
} from 'store/vat/models';
import { HmrcSubmissionRequest } from 'store/vat/models/hmrcSubmissionRequest';
import {
  BoxSummary,
  TaxReportSubsectionDataResponse,
} from 'store/vat/models/vatReportData';
import {
  getExcelFileData,
  getUpdateFromPage,
  getVatActivityLogs,
  getVatLoading,
  getVatNoDataFound,
  getVatPrepLoading,
  getVatReconciliation,
  getVatReportData,
  getVatReturnLogs,
  getVatRunData,
  getVatSuccess,
  getVatAttachmentLoading,
  getVatBackendError,
} from 'store/vat/selectors';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import {
  DataLogActions,
  DataLogStatus,
  DrawerType,
  LicenseType,
  TaxReportCountry,
  VatTaxReportType,
  ViesStatus,
  WindowType,
} from 'store/app/enums';
import {
  ActivityRoleUpdateRequest,
  JSONModifiedTrn,
  JSONTotals,
  JSONTotalsRecapCyprus,
  JSONTotalsRecapMalta,
  VatLoadRequestData,
  VatRunRequestData,
  ViewRunRequestData,
} from 'store/vat/models/requestData';
import {
  turnDrawerOn,
  turnMessageOn,
  turnModalOff,
  turnModalOn,
} from 'store/app/actions';
import { MessageStates } from 'containers/MessageBox';
import { DrawerData, MessageData, ModalData } from 'store/app/types';
import { checkResponse } from 'store/utils';
import { ConfirmBoxContainer } from 'containers/ConfirmBox';
import { hasReportWorkflowContext } from 'components/Vat/Report/report-workflow';
import { ClientApproveForm } from 'components/Vat/Report/client-approve-form';
import { EmailDataParametersRequest } from 'store/vat/models/emailDataParametersRequest';
import { NotApproveForm } from 'components/Vat/Report/not-approve-form';
import {
  getOrgMappedOrganisations,
  getOrgMappings,
  getOrgOrganisations,
} from 'store/organisation/selectors';
import { useTranslation } from 'react-i18next';
import { getUserMembers } from 'store/user/selectors';
import { ConnectEntity } from 'components/ConnectEntity';
import { AccountType, Provider, ReturnFrequency } from 'store/api/enums';
import { getReturnFrequencyLabel } from 'i18n/returnFrequencyLabel';
import { checkPageLoaded } from 'utils/check-page-loaded';
import { hasUnlimitedLicense } from 'utils/subscriptionPlanGate';
import { renderErrorMessage } from 'utils/render-error-message';
import { parseLocaleNumber } from 'utils/numberFormat';
import { AdjustmentForm } from 'components/Vat/Report/adjustments-form';
import { DataContact, DataJournal } from 'store/vat/models/vatReportData';
import {
  buildMaltaRecapPipeFile,
  downloadTextFile,
} from 'utils/export-malta-recap-pipe';
import {
  Preference,
  PreferenceActions,
  usePreferences,
  PreferenceSections,
} from 'utils/preferences';
import {
  AllExceptionsDetails,
  ExceptionsActionType,
  ExceptionsWrapper,
} from 'store/vat/models/exceptionWrapper';
import { getMenuSize } from 'store/app/selectors';
import {
  getCurrentSubscription,
  getSubscriptionPlanTypes,
  getBillingHasAdvancedRights,
} from 'store/billing/selectors';
import { TifdReturnForm } from 'components/Vat/Report/tifd/tifd-return-form';
import { fillTifdWorkbookRequest, loadTifdReportRequest } from 'store/tifd/actions';
import { splitActivityUsers, findReturnLog, savedRoleIds } from 'store/tifd/activity-users';
import {
  getTifdContext,
  getTifdError,
  getTifdLoading,
  getTifdReport,
  getTifdWorkbookLoading,
} from 'store/tifd/selectors';
import {
  PostJournalForm,
  PostJournalFormAction,
} from 'components/Vat/Report/post-journal-form';
import { fetchAccountsRequest } from 'store/organisation/actions';
import { hasAdvancedUserRight } from 'utils/hasAdvancedUserRight';
import { getIs3rdPartyProvider } from 'utils/get-provider-type';
import { AdvancedUserRightsCodes } from 'store/app/enums';
import GenTaxImage from 'assets/images/gen-tax-img.svg';
import HmrcLogoBig from 'assets/images/HMRCLogoBig.png';
import { useHistoryBlock } from 'hooks/useHistoryBlock';
const builder = require('xmlbuilder');
import { v4 as uuidv4 } from 'uuid';
import {
  beginCancelReturnNavigation,
  consumeCancelReturnNavigation,
} from './cancel-return-navigation';

const journalApplicationConnectionId = (journal: DataJournal): string | null =>
  journal.applicationConnectionId ||
  (journal as DataJournal & { applicationConnectionID?: string })
    .applicationConnectionID ||
  null;

const buildExceptionWrapper = (
  journal: DataJournal,
  exceptionActionType: string,
  adjustmentPeriodId: string | null = null,
  adjustDate: any = null
): ExceptionsWrapper => ({
  id: journal.sortId,
  exceptionActionType,
  adjustmentPeriodId,
  adjustDate: adjustDate ?? null,
  applicationConnectionId: journalApplicationConnectionId(journal),
});

export const PAGE_SIZE_TRANSACTIONS = 'page-size-transactions';
export const PAGE_SIZE_EXCEPTIONS = 'page-size-exceptions';
export const PAGE_SIZE_RECAP = 'page-size-recap';
export const FILTERS_TRANSACTIONS = 'filters-transactions';
export const FILTERS_EXCEPTIONS = 'filters-exceptions';

interface Params {
  connectionId: string;
  mappingId: string;
  dataLogId: string;
}

export const TifdReportPage: React.FC = () => {
  const DATE_FORMAT = 'DD/MM/YYYY HH:mm:ss';
  const vatRunData = useSelector(getVatRunData);
  const tifdReport = useSelector(getTifdReport);
  const tifdContext = useSelector(getTifdContext);
  const tifdWorkbookLoading = useSelector(getTifdWorkbookLoading);
  const tifdLoading = useSelector(getTifdLoading);
  const tifdError = useSelector(getTifdError);
  const vatReportData = useSelector(getVatReportData);
  const members = useSelector(getUserMembers);
  const reconciliation = useSelector(getVatReconciliation);
  const activityLogs = useSelector(getVatActivityLogs);
  const prepLoading = useSelector(getVatPrepLoading);
  const noDataFound = useSelector(getVatNoDataFound);
  const loading = useSelector(getVatLoading);
  const attachmentLoading = useSelector(getVatAttachmentLoading);
  const success = useSelector(getVatSuccess);
  const currentUser = useSelector(getAuthUser);
  const menuSize = useSelector(getMenuSize);
  const mappedOrganisations = useSelector(getOrgMappedOrganisations);
  const currentSubscription = useSelector(getCurrentSubscription);

  const mappings = useSelector(getOrgMappings);
  const excelFileData = useSelector(getExcelFileData);
  const organisations = useSelector(getOrgOrganisations);
  const selectedVatReturnLogs = useSelector(getVatReturnLogs);
  const updatingFromPage = useSelector(getUpdateFromPage);
  const subscriptionPlanTypes = useSelector(getSubscriptionPlanTypes);
  const hasAdvancedUserRightsPre = useSelector(getBillingHasAdvancedRights);
  const userLicence = useSelector(getAuthUserLicence);
  const [hasAdvancedUserRights, setHasAdvancedUserRights] =
    useState<boolean>(false);
  const [comparedReportData, setComparedReportData] =
    useState<VatReportData | null>(null);
  const isLoadingComparedRef = React.useRef<boolean>(false);
  const comparedDataLogIdRef = React.useRef<string | null>(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams() as Readonly<Params>;
  const connectionId = params.connectionId;
  const mappingId = params.mappingId;
  const dataLogId = params.dataLogId;
  const backendError = useSelector(getVatBackendError);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [percent, setPercent] = React.useState<number>(0);
  const [resetValues, setResetValues] = React.useState(false);
  const [pages, setPages] = React.useState<number>(4);
  const [generatedDate, setGeneratedDate] = React.useState<string>(
    moment().format(DATE_FORMAT)
  );
  const [generatedDateUTC, setGeneratedDateUTC] = React.useState<string>(
    moment().utc().format(DATE_FORMAT)
  );
  const [cannotSave, setCannotSave] = React.useState<boolean>(false);
  const [handledExceptions, setHandledExceptions] = React.useState<
    ExceptionsWrapper[]
  >([]);
  const [orginalStatus, setOriginalStatus] = React.useState<any>();
  const [reportId, setReportId] = React.useState<string>(
    uuidv4().replaceAll('-', '')
  );
  const [allExceptionsDetails, setAllExceptionsDetails] =
    React.useState<AllExceptionsDetails | null>(null);
  const allExceptionsDetailsRef = useRef(allExceptionsDetails);
  allExceptionsDetailsRef.current = allExceptionsDetails;
  const handledExceptionsRef = useRef(handledExceptions);
  handledExceptionsRef.current = handledExceptions;
  const [runReportTotalTaxTransactions, setRunReportTotalTaxTransactions] =
    React.useState<number | undefined>(undefined);
  const [runReportTotalTaxExceptions, setRunReportTotalTaxExceptions] =
    React.useState<number | undefined>(undefined);
  const [journalType, setJournalType] = React.useState<string>(
    vatReportData && vatReportData.summarizedSize != 0
      ? 'SUMMARISED'
      : 'DETAILED'
  );
  const userChoseJournalTypeRef = React.useRef(false);

  const handleSetJournalType = React.useCallback((type: string) => {
    userChoseJournalTypeRef.current = true;
    setJournalType(type);
  }, []);

  // Reset default when a new report session starts.
  React.useEffect(() => {
    userChoseJournalTypeRef.current = false;
    setJournalType(
      (vatReportData?.summarizedSize ?? 0) > 0 ? 'SUMMARISED' : 'DETAILED'
    );
  }, [reportId]);

  // When summarisation finishes, default to Summarised unless the user already picked a view.
  React.useEffect(() => {
    if (userChoseJournalTypeRef.current) {
      return;
    }
    if ((vatReportData?.summarizedSize ?? 0) > 0) {
      setJournalType('SUMMARISED');
    }
  }, [vatReportData?.summarizedSize]);

  // Redirect STAFF users without create submission rights back to main with a warning
  React.useEffect(() => {
    if (!currentUser || !organisations || !vatRunData) return;
    const selectedOrg = organisations.find(
      (o) => o.connectionId === vatRunData.tokenId
    );
    const isStaff = AccountType[currentUser.accountType] === AccountType.STAFF;
    const hasRight =
      !hasAdvancedUserRights ||
      hasAdvancedUserRight(
        currentUser,
        selectedOrg as any,
        AdvancedUserRightsCodes.CREATE_SUBMISSION
      );
    if (isStaff && !hasRight && !dataLogId) {
      const message: MessageData = {
        title: t('common.attention'),
        description: t('global.create-submission-rights-warning'),
        type: MessageStates.WARNING,
      };

      dispatch(turnMessageOn(message));
      navigate(routes.main, { replace: true });
    }
  }, [currentUser, organisations, vatRunData, hasAdvancedUserRights]);

  React.useEffect(() => {
    setHasAdvancedUserRights(
      hasAdvancedUserRightsPre ||
        userLicence?.licenseType === LicenseType.TRIAL ||
        hasUnlimitedLicense(userLicence)
    );
  }, [hasAdvancedUserRightsPre, userLicence]);

  useEffect(() => {
    const TWELVE_MINUTES = 12 * 60 * 1000;

    const interval = setInterval(() => {
      if (reportId) {
        console.log('heartbeat for', reportId);
        dispatch(touchReport(reportId));
      }
    }, TWELVE_MINUTES);

    return () => {
      clearInterval(interval);
    };
  }, [reportId]);

  const preferences = usePreferences();
  const handlePreferences = (
    preferenceSection: PreferenceSections,
    action: PreferenceActions,
    preference: Preference
  ) => {
    if (currentUser) {
      const savedPref = preferences(
        action,
        preferenceSection,
        preference,
        currentUser?.userUuid
      );
      return savedPref;
    }
    return false;
  };
  const setUpdatingFromPage = (value: boolean) =>
    dispatch(setUpdateFromPageRequest(value));
  const resetFinished = () => setResetValues(false);
  /** Avoid duplicate loadReport/generateReport when vatRunData is unchanged (e.g. org list refetch). */
  const lastReloadReportKeyRef = useRef<string | null>(null);

  const selectedMapping = mappings?.find(
    (m) => m.mappingId === vatRunData?.mappingId
  );

  const retFreq = selectedMapping?.returnFrequency
    ? selectedMapping?.returnFrequency
    : '';

  const returnTypeId = selectedMapping?.returnTypeId
    ? selectedMapping?.returnTypeId
    : '';
  const isClientUser =
    !!currentUser &&
    (currentUser.accountType === AccountType.CLIENT ||
      AccountType[currentUser.accountType] === AccountType.CLIENT);

  const goBack = () => {
    navigate(-1);
  };

  const reloadReportData = (
    data: VatRunData,
    isSaved: boolean,
    refresh: boolean
  ) => {
    const savedTransactionsRowsPerPage = handlePreferences(
      PreferenceSections.TRANSACTIONS,
      PreferenceActions.GET,
      { name: PAGE_SIZE_TRANSACTIONS }
    );
    const savedExceptionsRowsPerPage = handlePreferences(
      PreferenceSections.EXCEPTIONS,
      PreferenceActions.GET,
      { name: PAGE_SIZE_EXCEPTIONS }
    );
    const prefetchTransactionsPageSize =
      typeof savedTransactionsRowsPerPage === 'number' &&
      savedTransactionsRowsPerPage > 0
        ? savedTransactionsRowsPerPage
        : 20;
    const prefetchExceptionsPageSize =
      typeof savedExceptionsRowsPerPage === 'number' &&
      savedExceptionsRowsPerPage > 0
        ? savedExceptionsRowsPerPage
        : 10;

    const fromDateFormat = data.fromDate.includes('-')
      ? 'YYYY-MM-DD'
      : 'DD/MM/YYYY';
    const vatGenerateRequestData: VatGenerateRequestData = {
      connectionId: data.tokenId,
      mappingId: data.mappingId,
      fromDate: moment(data.fromDate, fromDateFormat).format('YYYY-MM-DD'),
      toDate: data.toDate,
      currency: data.currency,
      dataLogId: data.dataLogId,
      provider: data?.providerName?.toLowerCase(),
    };

    const willGenerate =
      data.dataLogId === undefined ||
      data.dataLogId === null ||
      (isSaved && !!data.dataLogId);
    const willLoadTotals =
      !!data.dataLogId &&
      !willGenerate &&
      !!data.status &&
      (DataLogStatus[data.status] === DataLogStatus.SUBMITTED ||
        DataLogStatus[data.status] === DataLogStatus.CANCELLED) &&
      data.taxType !== VatTaxReportType.RECAP &&
      !!data.totalsSaved;
    const willLoadReport =
      !!data.dataLogId && !willGenerate && !willLoadTotals && !data.updateFromProvider;

    // After save-with-refresh, updateFromProvider suppresses load/generate but this
    // function used to still reset DOM values — wiping the VAT return table to 0.00.
    if (!willGenerate && !willLoadTotals && !willLoadReport) {
      return;
    }

    setResetValues(true);
    setPercent(0);

    let pages = 0;
    if (isClientUser) {
      pages = 2;
    } else {
      pages = 4;

      if (data.taxType === VatTaxReportType.RECAP) pages = 3;
    }
    setPages(pages);
    if (willGenerate) {
      setRunReportTotalTaxTransactions(undefined);
      setRunReportTotalTaxExceptions(undefined);
      const journalsTotal =
        !vatReportData || !vatReportData.journals
          ? 0
          : vatReportData.detailedSize ?? 0;
      const exceptionsTotal =
        !vatReportData || !vatReportData.exceptions
          ? 0
          : vatReportData.exceptionsSize;
      const totals = journalsTotal + exceptionsTotal;

      if (!refresh) {
        // Always rotate report id for each fresh load/generate cycle
        // so backend query state cannot leak across different fetch contexts.

        const uuid = uuidv4().replaceAll('-', '');
        setReportId(uuid);

        dispatch(
          generateReportRequest(
            vatGenerateRequestData,
            totals,
            uuid,
            prefetchTransactionsPageSize,
            prefetchExceptionsPageSize,
            true
          )
        );
      } else {
        dispatch(
          refreshReportRequest(
            vatGenerateRequestData,
            reportId,
            totals,
            prefetchTransactionsPageSize,
            prefetchExceptionsPageSize,
            journalType,
            true
          )
        );
      }
    } else {
      const vatLoadRequestData: VatLoadRequestData = {
        ...vatGenerateRequestData,
        dataLogId: data.dataLogId,
      };

      if (willLoadTotals) {
        dispatch(loadReportTotalsRequest(data.mappingId, data.dataLogId));
      } else if (willLoadReport) {
        const uuid = uuidv4().replaceAll('-', '');
        setReportId(uuid);

        dispatch(
          loadReportRequest(
            vatLoadRequestData,
            uuid,
            undefined,
            prefetchTransactionsPageSize,
            prefetchExceptionsPageSize,
            true
          )
        );
      }
      const selectedOrg = organisations?.find(
        (o) => o.connectionId === data?.tokenId
      );
      currentUser &&
        !isClientUser &&
        hasAdvancedUserRights &&
        selectedOrg &&
        hasAdvancedUserRight(
          currentUser,
          selectedOrg as any,
          AdvancedUserRightsCodes.CREATE_SUBMISSION
        ) &&
        dispatch(fetchActivityLogsRequest(data.dataLogId));
    }
    //const connectedProperly = mappedOrganisations?.find(
    //  (mo) => mo.connectionId === data.tokenId
    //)?.connectedProperly;

    //data.taxType !== VatTaxReportType.RECAP &&
    //connectedProperly &&
    //currentUser &&
    //!isClientUser &&
    //dispatch(loadReconRequest(data.tokenId, data.toDate));
  };

  const saveReturn = (data: VatRunData) => {
    if (
      data.status &&
      DataLogStatus[data.status] === DataLogStatus.EXCEPTIONS_MODIFIED
    ) {
      saveExceptionAction();
    } else {
      const jsonTotals: Array<
        JSONTotals | JSONTotalsRecapMalta | JSONTotalsRecapCyprus | any
      > = [];
      let jsonModifiedTrns: JSONModifiedTrn[] = [];
      if (data.taxType !== VatTaxReportType.RECAP) {
        vatReportData?.subsections?.forEach((ss) =>
          jsonTotals.push({
            boxName: ss.code,
            boxValue: ss.net.toFixed(2),
            boxTaxValue: ss.tax.toFixed(2),
          })
        );
      } else {
        if (
          data.taxReportCountry === TaxReportCountry.MALTA ||
          data.taxReportCountry === TaxReportCountry.IRELAND_RECAP
        ) {
          vatReportData?.contacts?.forEach((c) => {
            const isTriangularTrade = c.isTriangularTrade;
            const name = c.name;
            const country = c.country;
            const taxNumber = c.taxNumber;
            const viesStatus = c.viesStatus;
            const goodsSum = c.goodsSum;
            const servicesSum = c.servicesSum;
            const providerContactId = c.providerContactId;

            jsonTotals.push({
              isTriangularTrade,
              name,
              country,
              taxNumber,
              viesStatus,
              goodsSum,
              servicesSum,
              providerContactId,
            });
          });
        }

        if (data.taxReportCountry === TaxReportCountry.CYPRUS) {
          vatReportData?.contacts?.forEach((c) => {
            const country = c.country;
            const taxNumber = c.taxNumber;
            const viesStatus = c.viesStatus;
            const goodsSum = c.goodsSum;
            const triangularTradeSum = c.triangularTradeSum;
            const servicesSum = c.servicesSum;
            jsonTotals.push({
              country,
              taxNumber,
              viesStatus,
              goodsSum,
              triangularTradeSum,
              servicesSum,
            });
          });
        }
      }

      /* const transactionsMap = new Map();
      getModifiedTransactions(
        vatReportData?.journals,
        TransactionType.TRANSACTION,
        transactionsMap
      );
      getModifiedTransactions(
        vatReportData?.exceptions,
        TransactionType.EXCEPTION,
        transactionsMap
      ); */

      jsonModifiedTrns = []; //Array.from(transactionsMap.values());
      const isUkReturn =
        data.taxReportCountry === TaxReportCountry.UK ||
        (data.taxReportTypeName ?? '').toLowerCase().includes('uk');
      const hmrcPeriodKey = isUkReturn
        ? data.obligation
            ?.map((obligation) => obligation?.periodKey?.trim())
            .find(
              (periodKey) => periodKey && periodKey.toLowerCase() !== 'none'
            )
        : undefined;

      const saveReturnRequestData: SaveReturnRequestData = {
        connectionId: data.tokenId,
        mappingId: data.mappingId,
        fromDate: data.fromDate,
        toDate: data.toDate,
        generatedDate: generatedDateUTC,
        currency: data.currency,
        reportingTypeId: data.reportingTypeId,
        dataLogId: data.dataLogId,
        taxType: data.taxType,
        jsonTotals,
        jsonModifiedTrns,
        totalTaxTransactions:
          data.dataLogId === undefined || data.dataLogId === null
            ? runReportTotalTaxTransactions ??
              vatReportData?.totalTaxTransactions ??
              0
            : 0,
        totalTaxExceptions:
          data.dataLogId === undefined || data.dataLogId === null
            ? runReportTotalTaxExceptions ??
              vatReportData?.totalTaxExceptions ??
              0
            : 0,
        // Backend expects counts names, but values come from report sizes.
        transactionsCount:
          data.dataLogId === undefined || data.dataLogId === null
            ? vatReportData?.transactionsSize ?? 0
            : 0,
        exceptionsCount:
          data.dataLogId === undefined || data.dataLogId === null
            ? vatReportData?.exceptionsSize ?? 0
            : 0,
        hmrcPeriodKey,
        provider: data?.providerName?.toLowerCase(),
      };

      setUpdatingFromPage(true);
      dispatch(cancelVatSaga());
      dispatch(saveReturnRequest(saveReturnRequestData, reportId));
    }
  };

  const cancelNotSaved = (data: VatRunData) => {
    const orgId = mappedOrganisations?.find(
      (mo) => mo.connectionId === data.tokenId
    )?.organisationId;
    orgId &&
      navigate(routes.organisations.organisation.replace(':id', orgId));
  };

  const revertData = (data: VatRunData) => {
    const viewRunRequestData: ViewRunRequestData = {
      connectionId: data.tokenId,
      mappingId: data.mappingId,
      dataLogId: data.dataLogId,
    };
    dispatch(fetchViewRunDataRequest(viewRunRequestData));
  };

  const cancelReturn = (data: VatRunData) => {
    const connectionId = data.tokenId;
    const mappingId = data.mappingId;
    const dataLogId = data.dataLogId;
    const organisationId = mappedOrganisations?.find(
      (mo) => mo.connectionId === connectionId
    )?.organisationId;

    beginCancelReturnNavigation();
    dispatch(turnModalOff());

    dispatch(
      cancelReturnRequest(dataLogId, connectionId, mappingId, organisationId)
    );
  };
  const getReportUrl = (data: VatRunData) =>
    process.env.PUBLIC_URL +
    routes.vat.tifdViewReportLink
      .replace(':connectionId', data.tokenId)
      .replace(':mappingId', data.mappingId)
      .replace(':dataLogId', data.dataLogId);

  const loadComparedReturn = async (compareTargetDataLogId: string) => {
    if (!vatRunData) {
      return;
    }

    isLoadingComparedRef.current = true;
    comparedDataLogIdRef.current = compareTargetDataLogId;

    const pickSubsections = (report: VatReportData | null | undefined) => {
      if (!report) {
        return [];
      }
      if (report.subsections?.length) {
        return report.subsections;
      }
      return report.adjustedSubsections ?? [];
    };

    try {
      const { loadReportTotals } = await import('store/vat/api');
      const { loadComparedReportViaView } = await import(
        'store/vat/loadComparedReportData'
      );

      const vatLoadRequestData: VatLoadRequestData = {
        connectionId: vatRunData.tokenId,
        mappingId: vatRunData.mappingId,
        dataLogId: compareTargetDataLogId,
        currency: vatRunData.currency,
        fromDate: vatRunData.fromDate,
        toDate: vatRunData.toDate,
        provider: vatRunData.providerName?.toLowerCase() ?? '',
      };

      const compareReportId = uuidv4().replaceAll('-', '');

      let comparedPayload: VatReportData | null = null;
      try {
        comparedPayload = await loadComparedReportViaView(
          vatLoadRequestData,
          compareReportId
        );
      } catch {
        comparedPayload = null;
      }

      if (!comparedPayload || !pickSubsections(comparedPayload).length) {
        const tr = await loadReportTotals(
          vatRunData.mappingId,
          compareTargetDataLogId
        );
        const trErr = checkResponse(tr?.responseMessage);
        if (trErr) {
          throw trErr;
        }
        const sourceSubsections = pickSubsections(tr.data);
        if (tr.data && sourceSubsections.length > 0) {
          const d = tr.data;
          comparedPayload = {
            ...d,
            journals: d.journals ?? [],
            exceptions: d.exceptions ?? [],
            subsections: sourceSubsections.map((sub) => ({ ...sub })),
            adjustedSubsections: d.adjustedSubsections
              ? d.adjustedSubsections.map((sub) => ({ ...sub }))
              : [],
            sections: d.sections ? d.sections.map((sec) => ({ ...sec })) : [],
          };
        }
      }

      if (comparedPayload && pickSubsections(comparedPayload).length > 0) {
        setComparedReportData(comparedPayload);
      } else {
        const message: MessageData = {
          title: t('vat-report.compare-failed-title'),
          description: t('vat-report.compare-no-box-totals-returned'),
          type: MessageStates.WARNING,
        };
        dispatch(turnMessageOn(message));
      }
    } catch (error: unknown) {
      const rendered = renderErrorMessage(error);
      const message: MessageData = rendered ?? {
        title: t('vat-report.compare-failed-title'),
        description: t('vat-report.compare-could-not-load-selected-return'),
        type: MessageStates.ERROR,
      };
      dispatch(
        turnMessageOn({
          ...message,
          title: message.title || t('vat-report.compare-failed-title'),
        })
      );
    } finally {
      isLoadingComparedRef.current = false;
    }
  };

  // No longer needed - we fetch compared data directly via API now
  // This prevents the form from updating with compared values because Redux vatReportData never changes

  const clearComparison = () => {
    setComparedReportData(null);
    isLoadingComparedRef.current = false;
    comparedDataLogIdRef.current = null;
  };

  const setAsAwaitingApproval = (data: VatRunData) => {
    if (!data.reviewerId) {
      const message: MessageData = {
        title: t('vat-report.reviewer-not-set-title'),
        description: t('vat-report.reviewer-not-set-description'),
        type: MessageStates.ERROR,
      };

      dispatch(turnMessageOn(message));
    } else {
      setUpdatingFromPage(true);
      dispatch(
        awaitingApprovalRequest(
          data.dataLogId,
          getReportUrl(data),
          data.mappingId
        )
      );
    }
  };

  const setInternallyApproved = (data: VatRunData) => {
    setUpdatingFromPage(true);
    dispatch(
      internallyApproveRequest(
        data.dataLogId,
        getReportUrl(data),
        data.mappingId
      )
    );
  };

  const sendForClientApproval = (values: EmailDataParametersRequest) => {
    if (vatRunData) {
      const emailUrl = getReportUrl(vatRunData);
      const emailData: EmailDataParametersRequest = { ...values, emailUrl };
      setUpdatingFromPage(true);
      dispatch(
        sendClientApprovalRequest(
          vatRunData.dataLogId,
          emailData,
          vatRunData.mappingId
        )
      );
      dispatch(turnModalOff());
    }
  };
  const sendForClientApprovalWindow = (data: VatRunData) => {
    if (canLock()) {
      if (!data.clientId) {
        const message: MessageData = {
          title: t('vat-report.client-not-set-title'),
          description: t('vat-report.client-not-set-description'),
          type: MessageStates.ERROR,
        };

        dispatch(turnMessageOn(message));
      } else {
        const drawerData: DrawerData = {
          title: t('common.send-to-client'),
          type: DrawerType.NOT_APPROVED_FORM,
          component: (
            <ClientApproveForm sendForClientApproval={sendForClientApproval} />
          ),
        };
        dispatch(turnDrawerOn(drawerData));
      }
    } else exceptionsWarning();
  };
  const exceptionsWarning = () => {
    const message: MessageData = {
      title: t('vat-report.exceptions-not-handled-title'),
      description: (
        <span>
          You have out of period transactions (exceptions) which have not yet
          been processed. You can:
          <br />
          <br />
          1) mark them to be included in the current submission
          <br />
          <br />
          2) adjust and include them a previous submission
          <br />
          <br />
          3) defer them to the next submission
          <br />
          <br />
          You can't submit this report until all exceptions are handled. You may{' '}
          <a
            href="https://help.generate.tax/portal/en/kb/articles/introduction-to-exceptions"
            target="_blank"
            rel="noreferrer"
          >
            read more
          </a>{' '}
          about Exceptions.
        </span>
      ),
      type: MessageStates.ERROR,
    };
    dispatch(turnMessageOn(message));
  };

  const contactsWarning = () => {
    const errorContacts: string[] = [];
    vatReportData?.contacts?.forEach((contact) => {
      if (
        !contact.taxNumber ||
        (contact.viesStatus &&
          ViesStatus[contact.viesStatus] !== ViesStatus.VALID)
      ) {
        if (errorContacts.findIndex((ec) => ec === contact.name) === -1)
          errorContacts.push(contact.name);
      }
    });
    const message: MessageData = {
      title: t('vat-report.missing-information-title'),
      description: missingTaxErrorMessage(errorContacts),
      type: MessageStates.ERROR,
    };
    dispatch(turnMessageOn(message));
  };

  const setClientApproved = (data: VatRunData) => {
    setUpdatingFromPage(true);
    dispatch(
      clientApproveRequest(data.dataLogId, getReportUrl(data), data.mappingId)
    );
  };

  const updateActivityRole = (requestData: ActivityRoleUpdateRequest) => {
    setUpdatingFromPage(true);
    dispatch(updateActivityRoleRequest(requestData));
  };

  const setNotApprovedWindow = () => {
    const drawerData: DrawerData = {
      title: t('common.add-note'),
      type: DrawerType.NOT_APPROVED_FORM,
      component: <NotApproveForm setNotApproved={setNotApproved} />,
    };
    dispatch(turnDrawerOn(drawerData));
  };

  const setNotApproved = (values: EmailDataParametersRequest) => {
    if (vatRunData) {
      const emailUrl = getReportUrl(vatRunData);
      const emailData: EmailDataParametersRequest = { ...values, emailUrl };
      setUpdatingFromPage(true);
      dispatch(
        notApproveRequest(vatRunData.dataLogId, emailData, vatRunData.mappingId)
      );
      dispatch(turnModalOff());
    }
  };

  const markReturnAsSubmitted = (data: VatRunData) => {
    const handleSubmit = (
      action: PostJournalFormAction,
      journalPostData?: JournalPostData
    ) => {
      //dispatch(turnModalOff());
      if (action !== PostJournalFormAction.CANCEL) {
        setUpdatingFromPage(true);
        if (action === PostJournalFormAction.POST && journalPostData) {
          dispatch(
            manualJournalPostRequest(
              data.dataLogId,
              data.mappingId,
              journalPostData
            )
          );
        } else {
          sessionStorage.setItem('just-submitted', 'true');
          dispatch(submitReturnRequest(data.dataLogId, data.mappingId));
          dispatch(turnModalOff());
        }
      } else dispatch(turnModalOff());
    };

    let receivableAmount: number | undefined = 0;
    let payableAmount: number | undefined = 0;
    let inputTax: number = 0;
    let outputTax: number = 0;

    if (data.taxType === VatTaxReportType.VAT_RETURN) {
      switch (data.taxReportCountry) {
        case TaxReportCountry.MALTA:
          receivableAmount = vatReportData?.subsections?.find((s) =>
            s.code.includes('42')
          )?.net;
          const box44Value = vatReportData?.subsections?.find((s) =>
            s.code.includes('44')
          )?.net;
          const box43Value = vatReportData?.subsections?.find((s) =>
            s.code.includes('43')
          )?.net;
          const box44 = box44Value ? box44Value : 0;
          const box43 = box43Value ? box43Value : 0;
          payableAmount = box43 - box44;

          inputTax = getBoxValue('39');
          outputTax = getBoxValue('26');

          break;
        case TaxReportCountry.CYPRUS:
          const vatAmountCyprus = vatReportData?.subsections?.find((s) =>
            s.code.includes('5')
          )?.net;

          inputTax = getBoxValue('4');
          outputTax = getBoxValue('3');

          if (vatAmountCyprus && vatAmountCyprus !== 0) {
            if (vatAmountCyprus < 0) {
              receivableAmount = Math.abs(vatAmountCyprus);
            } else {
              payableAmount = Math.abs(vatAmountCyprus);
            }
          }
          break;
        case TaxReportCountry.UAE:
          const vatAmountUae = vatReportData?.subsections?.find((s) =>
            s.code.includes('14')
          )?.net;

          inputTax = getBoxValue('13');
          outputTax = getBoxValue('12');

          if (vatAmountUae && vatAmountUae !== 0) {
            if (vatAmountUae < 0) {
              receivableAmount = Math.abs(vatAmountUae);
            } else {
              payableAmount = Math.abs(vatAmountUae);
            }
          }
          break;
        case TaxReportCountry.IRELAND_STANDARD:
          const boxT3 = vatReportData?.subsections?.find((s) =>
            s.code.includes('T3')
          )?.net;
          const boxT4 = vatReportData?.subsections?.find((s) =>
            s.code.includes('T4')
          )?.net;
          receivableAmount = boxT4 ? Math.abs(boxT4) : 0;
          payableAmount = boxT3 ? Math.abs(boxT3) : 0;

          inputTax = Math.abs(getBoxValue('T2'));
          outputTax = Math.abs(getBoxValue('T1'));

          break;
        case TaxReportCountry.NETHERLANDS:
          const vatAmountNetherlands = vatReportData?.subsections?.find((s) =>
            s.code.includes('5c')
          )?.net;
          if (vatAmountNetherlands && vatAmountNetherlands !== 0) {
            if (vatAmountNetherlands < 0) {
              receivableAmount = Math.abs(vatAmountNetherlands);
            } else {
              payableAmount = Math.abs(vatAmountNetherlands);
            }
          }

          inputTax = Math.abs(getBoxValue('5b'));
          outputTax = Math.abs(getBoxValue('5a'));
          break;
        case TaxReportCountry.JERSEY:
          const vatAmountJersey = vatReportData?.subsections?.find((s) =>
            s.code.includes('8')
          )?.net;
          if (vatAmountJersey && vatAmountJersey !== 0) {
            if (vatAmountJersey < 0) {
              receivableAmount = Math.abs(vatAmountJersey);
            } else {
              payableAmount = Math.abs(vatAmountJersey);
            }
          }

          inputTax = Math.abs(getBoxValue('7'));
          outputTax = Math.abs(getBoxValue('6'));
          break;
        default:
          break;
      }
    }

    const isProviderGroupEntity =
      data.providerName?.toLowerCase() === Provider.GROUP.toLowerCase();

    if (
      getIs3rdPartyProvider(data.providerName) &&
      ((receivableAmount && receivableAmount !== 0) ||
        (payableAmount && payableAmount !== 0)) &&
      (data.taxReportCountry === TaxReportCountry.MALTA ||
        data.taxReportCountry === TaxReportCountry.CYPRUS ||
        data.taxReportCountry === TaxReportCountry.UAE ||
        data.taxReportCountry === TaxReportCountry.IRELAND_STANDARD ||
        data.taxReportCountry === TaxReportCountry.NETHERLANDS ||
        data.taxReportCountry === TaxReportCountry.JERSEY) &&
      !isProviderGroupEntity &&
      (!vatReportData?.contacts || vatReportData?.contacts.length === 0)
    ) {
      refreshAccounts();
      const modalData: ModalData = {
        maskClosable: false,
        type: WindowType.POST_JOURNAL,
        title: t('vat-report.tax-submission'),
        component: (
          <PostJournalForm
            data={data}
            handleSubmit={handleSubmit}
            reconciliation={reconciliation}
            receivableAmount={receivableAmount ? receivableAmount : 0}
            payableAmount={payableAmount ? payableAmount : 0}
            refreshAccounts={refreshAccounts}
            handlePreferences={handlePreferences}
            providerName={vatRunData?.providerName?.toLowerCase()}
            inputTax={inputTax}
            outputTax={outputTax}
          />
        ),
      };

      dispatch(turnModalOn(modalData));
    } else handleSubmit(PostJournalFormAction.SUBMIT);
  };

  const getBoxValue = (code: string) => {
    return (
      vatReportData?.subsections?.find((s) => s.code.includes(code))?.net || 0
    );
  };
  const refreshAccounts = () => {
    vatRunData &&
      dispatch(fetchAccountsRequest(vatRunData.tokenId, vatRunData.mappingId));
  };

  const markReturnAsLocked = (data: VatRunData) => {
    setUpdatingFromPage(true);
    dispatch(lockReturnRequest(data.dataLogId, data.mappingId));
  };

  const updateNote = (noteText: string, dataLogId: string) => {
    if (!noteText || noteText === '') {
      const message: MessageData = {
        title: t('main.reconciliation-empty-note-title'),
        description: t('main.reconciliation-empty-note-desc'),
        type: MessageStates.WARNING,
      };

      dispatch(turnMessageOn(message));
    } else {
      dispatch(addNoteRequest(noteText, dataLogId));
    }
  };

  const cancelWarningWindow = (data: VatRunData) => {
    const modalData: ModalData = {
      type: WindowType.WARNING,
      title: t('vat-report.cancel-report-title'),
      icon: <img src={GenTaxImage} alt="" />,
      component: (
        <ConfirmBoxContainer
          callback={cancelReturn}
          param={data}
          description={t('vat-report.cancel-report-description')}
        />
      ),
    };

    dispatch(turnModalOn(modalData));
  };
  const LOCK = 'LOCK';
  const ERROR_CONTACTS = 'ERROR_CONTACTS';

  const canLock = () => {
    let canLock = LOCK;
    if (
      (vatRunData?.taxReportCountry === TaxReportCountry.IRELAND_RECAP ||
        vatRunData?.taxReportCountry === TaxReportCountry.MALTA) &&
      vatReportData?.contacts &&
      vatReportData?.contacts.length > 0 &&
      vatReportData?.contacts.findIndex(
        (contact) =>
          !contact.taxNumber ||
          (contact.viesStatus &&
            ViesStatus[contact.viesStatus] !== ViesStatus.VALID)
      ) !== -1
    ) {
      canLock = ERROR_CONTACTS;
    }
    return canLock;
  };

  const loackWarningWindow = (
    vatRunData: VatRunData,
    actionType: DataLogActions
  ) => {
    const canLockMessage = canLock();
    if (canLockMessage === LOCK) {
      const country = (vatRunData.taxReportCountry ?? '')
        .toString()
        .trim()
        .toLowerCase();
      const isUkReturn =
        country === TaxReportCountry.UK.toLowerCase() ||
        country === 'united kingdom' ||
        country === 'uk' ||
        (vatRunData.taxReportTypeName ?? '').toLowerCase().includes('uk');
      const isHmrcSubmission =
        actionType == DataLogActions.MARK_AS_SUBMITTED && isUkReturn;

      let callback;
      if (actionType == DataLogActions.LOCK_AS_APPROVED) {
        callback = () => markReturnAsLocked(vatRunData);
      } else if (actionType == DataLogActions.MARK_AS_SUBMITTED) {
        callback = () =>
          isUkReturn ? submitToHmrc() : markReturnAsSubmitted(vatRunData);
      }

      // HMRC logo is wired up but hidden for now; set to true to show it.
      const showHmrcLogo = false;

      const modalData: ModalData = {
        type: WindowType.WARNING,
        title: isHmrcSubmission
          ? t('vat-report.submit-to-hmrc')
          : t('vat-report.lock-tax-submission-title'),
        icon: isHmrcSubmission
          ? showHmrcLogo
            ? (
                <img
                  src={HmrcLogoBig}
                  alt="HMRC"
                  style={{ width: 120, height: 'auto' }}
                />
              )
            : undefined
          : (
              <img src={GenTaxImage} alt="" />
            ),
        component: (
          <ConfirmBoxContainer
            callback={callback}
            showCancelBtn={true}
            param2={vatRunData}
            okText={
              isHmrcSubmission ? t('common.confirm') : t('common.proceed')
            }
            description={
              isHmrcSubmission
                ? t('vat-report.hmrc-client-approval-declaration')
                : t('vat-report.confirm-reviews-data-locked')
            }
          />
        ),
      };

      dispatch(turnModalOn(modalData));
    } else if (canLockMessage === ERROR_CONTACTS) contactsWarning();
  };

  const skipUnsavedNavigationBlockRef = useRef(false);
  const blockedProceedRef = useRef<(() => void) | undefined>(undefined);
  const unsavedNavigationModalShownRef = useRef(false);

  const blocker = useHistoryBlock(() => {
    if (skipUnsavedNavigationBlockRef.current) {
      return true;
    }

    if (consumeCancelReturnNavigation()) {
      dispatch(cancelVatSaga());
      sessionStorage.removeItem('just-submitted');
      if (!dataLogId) {
        sessionStorage.removeItem('last-created-report');
      }
      return true;
    }

    if (
      (!vatRunData?.status ||
        DataLogStatus[vatRunData.status] === DataLogStatus.NOT_SAVED ||
        DataLogStatus[vatRunData.status] === DataLogStatus.NOT_STARTED ||
        DataLogStatus[vatRunData.status] ===
          DataLogStatus.EXCEPTIONS_MODIFIED) &&
      !cannotSave &&
      vatRunData &&
      hasReportWorkflowContext(vatRunData, vatReportData, success)
    ) {
      return false;
    }
    dispatch(cancelVatSaga());

    sessionStorage.removeItem('just-submitted');

    if (!dataLogId) {
      sessionStorage.removeItem('last-created-report');
    }

    return true;
  });

  useEffect(() => {
    if (blocker.state === 'blocked' && blocker.proceed) {
      blockedProceedRef.current = blocker.proceed;
      return;
    }

    blockedProceedRef.current = undefined;
    unsavedNavigationModalShownRef.current = false;
  }, [blocker.state, blocker.proceed]);

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      return;
    }
    if (!vatRunData || unsavedNavigationModalShownRef.current) {
      return;
    }
    unsavedNavigationModalShownRef.current = true;

    const continueWithoutSaving = () => {
      skipUnsavedNavigationBlockRef.current = true;
      unsavedNavigationModalShownRef.current = false;
      sessionStorage.removeItem('last-created-report');
      const proceed = blockedProceedRef.current;
      const nextLocation = blocker.location;
      blockedProceedRef.current = undefined;
      if (proceed) {
        proceed();
      } else if (nextLocation) {
        blocker.reset?.();
        navigate({
          pathname: nextLocation.pathname,
          search: nextLocation.search || '',
        });
      }
      dispatch(cancelVatSaga());
    };

    const saveReturnFromBlocker = (data: VatRunData) => {
      unsavedNavigationModalShownRef.current = false;
      blocker.reset?.();
      saveReturn(data);
    };

    const modalData: ModalData = {
      type: WindowType.WARNING,
      title: t('vat-report.report-not-saved-title'),
      icon: <img src={GenTaxImage} alt="" />,
      component: (
        <ConfirmBoxContainer
          callback={continueWithoutSaving}
          callback2={saveReturnFromBlocker}
          showCancelBtn={false}
          param2={vatRunData}
          okText={t('vat-report.continue-without-saving')}
          okText2={t('vat-report.save-submission')}
          description={t('vat-report.go-back-unsaved-report-description')}
        />
      ),
    };

    dispatch(turnModalOn(modalData));
  }, [blocker.state, vatRunData, dispatch]);

  const onReconnect = () => {
    const drawerData: DrawerData = {
      title: t('organisation.connect-entity-title'),
      subtitle: t('organisation.connect-entity-subtitle'),
      type: DrawerType.CONNECT_ENTITY,
      component: <ConnectEntity />,
    };
    dispatch(turnDrawerOn(drawerData));
  };

  useEffect(() => {
    if (
      vatRunData &&
      organisations &&
      (!vatRunData.status ||
        DataLogStatus[vatRunData.status] !== DataLogStatus.EXCEPTIONS_MODIFIED)
    ) {
      const noSave =
        (!vatRunData.isPeriodClosed && !vatRunData.dataLogId) ||
        (vatRunData.status !== DataLogStatus.CANCELLED &&
          vatRunData.status !== DataLogStatus.SUBMITTED &&
          vatRunData.isFirstTime === false &&
          vatRunData.validRange !== null &&
          vatRunData.validRange === false);
      setCannotSave(noSave);

      if (
        !tifdReport &&
        (!vatRunData.status ||
          DataLogStatus[vatRunData.status] !== DataLogStatus.NOT_SAVED)
      ) {
        dispatch(fetchVatLogsRequest(vatRunData.tokenId, [vatRunData.mappingId]));
        if (updatingFromPage) {
          setUpdatingFromPage(false);
        } else {
          const reloadKey = [
            vatRunData.tokenId,
            vatRunData.mappingId,
            vatRunData.dataLogId ?? '',
            vatRunData.status ?? '',
            String(vatRunData.updateFromProvider ?? ''),
          ].join('|');
          if (
            lastReloadReportKeyRef.current !== reloadKey &&
            !vatRunData.updateFromProvider
          ) {
            lastReloadReportKeyRef.current = reloadKey;
            reloadReportData(vatRunData, false, false);
          }
        }

        if (vatRunData.dataLogId) {
          setGeneratedDate(
            moment
              .utc(vatRunData.generatedDate, DATE_FORMAT)
              .local()
              .format(DATE_FORMAT)
          );
        }
      } else if (tifdReport && vatRunData.generatedDate) {
        lastReloadReportKeyRef.current = null;
        setGeneratedDate(
          moment
            .utc(vatRunData.generatedDate, DATE_FORMAT)
            .local()
            .format(DATE_FORMAT)
        );
        setGeneratedDateUTC(vatRunData.generatedDate);
      } else {
        lastReloadReportKeyRef.current = null;
        setGeneratedDate(moment().format(DATE_FORMAT));
        setGeneratedDateUTC(moment().utc().format(DATE_FORMAT));
      }
    }
  }, [vatRunData, members, organisations, tifdReport]);

  useEffect(() => {
    if (backendError) {
      setPercent(100);
      return;
    }

    // Latch complete once generation succeeds — ignore transient `loading`
    // toggles from ancillary requests (e.g. vat return logs).
    if (success || (tifdReport && vatRunData)) {
      setPercent(100);
      return;
    }

    if (vatReportData) {
      if (vatReportData.totalPages === 0 || !vatReportData.totalPages) {
        setPercent(100);
      } else {
        const singlePage = Math.round(100 / vatReportData.totalPages);
        const percentage =
          vatReportData.totalPages === vatReportData.page
            ? 100
            : singlePage * (vatReportData.page ?? 0);
        setPercent(percentage);
      }
    } else {
      setPercent(0);
    }
  }, [
    JSON.stringify(vatReportData),
    JSON.stringify(vatRunData),
    backendError,
    success,
    tifdReport,
  ]);

  useEffect(() => {
    if (
      !dataLogId &&
      runReportTotalTaxTransactions === undefined &&
      runReportTotalTaxExceptions === undefined &&
      vatReportData?.page === 1 &&
      typeof vatReportData?.totalTaxTransactions === 'number' &&
      typeof vatReportData?.totalTaxExceptions === 'number'
    ) {
      setRunReportTotalTaxTransactions(vatReportData.totalTaxTransactions);
      setRunReportTotalTaxExceptions(vatReportData.totalTaxExceptions);
    }
  }, [
    dataLogId,
    runReportTotalTaxTransactions,
    runReportTotalTaxExceptions,
    vatReportData,
  ]);

  const runDataFetchStartedRef = useRef(false);
  const loadedTifdReportIdRef = useRef<string | null>(null);
  const appliedRoleLogIdRef = useRef<string | null>(null);

  // A saved TIFD url loads GET /tifd/{organisationId}/reports/{reportId}.
  // The log segment is the TIFD report id, not a VAT data log.
  useEffect(() => {
    lastReloadReportKeyRef.current = null;

    if (dataLogId) {
      sessionStorage.removeItem('last-created-report');
      runDataFetchStartedRef.current = false;
      if (!connectionId || !mappingId || !organisations?.length) return;
      if (loadedTifdReportIdRef.current === dataLogId) return;
      const organisation = organisations.find(
        (item) => item.connectionId === connectionId
      );
      if (!organisation?.uuid) return;
      loadedTifdReportIdRef.current = dataLogId;
      dispatch(
        loadTifdReportRequest({
          applicationConnectionId: organisation?.uuid ?? '',
          reportId: dataLogId,
          connectionId,
          mappingId,
          currency: organisation?.currency,
          providerName: organisation?.providerName,
          connectionName: organisation?.friendlyName,
          shortCode: organisation?.shortCode,
        })
      );
    } else {
      loadedTifdReportIdRef.current = null;
      const lastCreatedReport = sessionStorage.getItem('last-created-report');
      if (!lastCreatedReport) {
        navigate(routes.main);
      } else if (!vatRunData && !tifdReport && !runDataFetchStartedRef.current) {
        runDataFetchStartedRef.current = true;
        const vatRunRequestData: VatRunRequestData =
          JSON.parse(lastCreatedReport);
        dispatch(fetchRunDataRequest(vatRunRequestData, undefined, false));
      }
    }
  }, [connectionId, mappingId, dataLogId, organisations, dispatch, navigate]);

  // The VAT view used to supply the role lists and who is already assigned.
  // Members fill the lists. The mapping data log already on the organisation
  // supplies the saved preparer, reviewer, and client.
  useEffect(() => {
    if (!tifdReport || !vatRunData?.dataLogId) return;
    const { staff, clients } = splitActivityUsers(members);
    const nextStaff =
      (vatRunData.staff?.length ?? 0) > 0 ? vatRunData.staff : staff;
    const nextClients =
      (vatRunData.clients?.length ?? 0) > 0 ? vatRunData.clients : clients;
    const log = findReturnLog(
      mappings,
      vatRunData.mappingId,
      vatRunData.dataLogId
    );
    const savedRoles = savedRoleIds(log, members);
    const applySavedRoles =
      !!log && appliedRoleLogIdRef.current !== vatRunData.dataLogId;
    if (applySavedRoles) {
      appliedRoleLogIdRef.current = vatRunData.dataLogId;
    }
    const preparerId = applySavedRoles
      ? vatRunData.preparerId || savedRoles.preparerId
      : vatRunData.preparerId;
    const reviewerId = applySavedRoles
      ? vatRunData.reviewerId || savedRoles.reviewerId
      : vatRunData.reviewerId;
    const clientId = applySavedRoles
      ? vatRunData.clientId || savedRoles.clientId || ''
      : vatRunData.clientId;
    const staffChanged =
      (vatRunData.staff?.length ?? 0) === 0 && (nextStaff?.length ?? 0) > 0;
    const clientsChanged =
      (vatRunData.clients?.length ?? 0) === 0 && (nextClients?.length ?? 0) > 0;
    const rolesChanged =
      (preparerId || '') !== (vatRunData.preparerId || '') ||
      (reviewerId || '') !== (vatRunData.reviewerId || '') ||
      (clientId || '') !== (vatRunData.clientId || '');
    if (!staffChanged && !clientsChanged && !rolesChanged) return;
    dispatch(
      fetchRunDataSuccess({
        ...vatRunData,
        staff: nextStaff,
        clients: nextClients,
        preparerId,
        reviewerId,
        clientId,
      })
    );
  }, [tifdReport, vatRunData, members, mappings, dispatch]);

  // Stuck on a fresh report route with no run data and no session marker — leave.
  useEffect(() => {
    if (dataLogId || vatRunData || tifdReport) {
      return;
    }
    if (!sessionStorage.getItem('last-created-report')) {
      navigate(routes.main, { replace: true });
    }
  }, [dataLogId, vatRunData, tifdReport, navigate]);

  // Clear VAT slice after leaving a fresh (unsaved) report route.
  useEffect(() => {
    return () => {
      skipUnsavedNavigationBlockRef.current = false;
      if (!dataLogId) {
        dispatch(cancelVatSaga());
        if (!sessionStorage.getItem('last-created-report')) {
          dispatch(resetVatData());
        }
      }
    };
  }, [dataLogId, dispatch]);

  useEffect(() => {
    currentUser && checkPageLoaded(location.pathname, routes.vat.root);
  }, [currentUser, location.pathname]);

  // Each page of data is being fetched separately from backend.
  // Once the page loads, override exception action type from backend with the one that user selected
  useEffect(() => {
    if (!vatReportData?.exceptions) return;

    let updated = false;

    let exceptions = vatReportData.exceptions;

    if (allExceptionsDetails?.actionType) {
      exceptions = exceptions.map((e) => ({
        ...e,
        exceptionActionType: allExceptionsDetails.actionType,
      }));
      updated = true;
    }

    if (handledExceptions?.length > 0) {
      const handledMap = new Map(
        handledExceptions.map((he) => [he.id, he.exceptionActionType])
      );
      exceptions = exceptions.map((e) => {
        const handledAction = handledMap.get(e.sortId);
        if (handledAction && e.exceptionActionType !== handledAction) {
          return { ...e, exceptionActionType: handledAction };
        }
        return e;
      });
      updated = true;
    }

    if (updated) {
      vatReportData.exceptions = exceptions;
    }
  }, [JSON.stringify(vatReportData), allExceptionsDetails, handledExceptions]);

  const openAdjustmentPanal = (
    selectedRowKeys: DataJournal[],
    setSelectedRowKey: (data: DataJournal[]) => void,
    allExceptionsSelected: boolean,
    setAllExceptionsSelected: (val: boolean) => void
  ) => {
    const drawerData: DrawerData = {
      title: t('exceptions.adjustment'),
      type: DrawerType.ADJUSTMENT_FORM,
      component: (
        <AdjustmentForm
          selectedRowKeys={selectedRowKeys}
          addExceptionAction={addExceptionAction}
          selectedVatReturnLogs={selectedVatReturnLogs}
          setSelectedRowKey={setSelectedRowKey}
          allExceptionsSelected={allExceptionsSelected}
          setAllExceptionsSelected={setAllExceptionsSelected}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  const addExceptionAction = (
    exceptionActionType: string,
    selectedRowKeys: DataJournal[],
    setSelectedRowKey: (data: DataJournal[]) => void,
    allExceptionsSelected: boolean,
    setAllExceptionsSelected: (val: boolean) => void,
    adjustmentPeriodId: string | null = null,
    adjustDate: any = null
  ) => {
    let tempExceptions = [...handledExceptionsRef.current];

    if (allExceptionsSelected) {
      setAllExceptionsDetails({
        actionType: exceptionActionType,
        adjustmentPeriodId,
        adjustDate,
      });
      allExceptionsDetailsRef.current = {
        actionType: exceptionActionType,
        adjustmentPeriodId,
        adjustDate,
      };
      // A new select-all replaces any previous per-row overrides.
      tempExceptions = [];
      setHandledExceptions([]);
      handledExceptionsRef.current = [];
      setAllExceptionsSelected(false);

      if (vatReportData?.exceptions) {
        vatReportData.exceptions = vatReportData.exceptions.map((e) => ({
          ...e,
          exceptionActionType,
        }));
      }
    } else {
      selectedRowKeys.forEach((srk) => {
        const procExeption = buildExceptionWrapper(
          srk,
          exceptionActionType,
          adjustmentPeriodId,
          adjustDate
        );
        const idx = tempExceptions.findIndex((te) => te.id === srk.sortId);

        if (
          ExceptionsActionType[exceptionActionType] ===
          ExceptionsActionType.UNRESOLVED
        ) {
          if (idx !== -1) tempExceptions.splice(idx, 1);
        } else {
          if (idx === -1) tempExceptions.push(procExeption);
          else tempExceptions.splice(idx, 1, procExeption);
        }
      });

      if (vatReportData?.exceptions) {
        const selectedUuids = new Set(selectedRowKeys.map((srk) => srk.uuid));
        vatReportData.exceptions = vatReportData.exceptions.map((vrd) => {
          if (selectedUuids.has(vrd.uuid)) {
            return { ...vrd, exceptionActionType };
          }
          const inHandled = tempExceptions.some(
            (he) => he.id === vrd.sortId
          );
          if (
            !selectedUuids.has(vrd.uuid) &&
            !inHandled &&
            !vrd.exceptionActionType
          ) {
            return { ...vrd, exceptionActionType: 'UNRESOLVED' };
          }
          return vrd;
        });
      }

      setHandledExceptions(tempExceptions);
      handledExceptionsRef.current = tempExceptions;
    }

    setSelectedRowKey([]);

    if (
      ExceptionsActionType[exceptionActionType] !==
      ExceptionsActionType.UNRESOLVED
    ) {
      let actionString = '';
      if (
        ExceptionsActionType[exceptionActionType] ===
        ExceptionsActionType.ADJUSTMENT
      )
        actionString = t('vat-report.adjustment-prior-period');
      if (
        ExceptionsActionType[exceptionActionType] ===
        ExceptionsActionType.CURRENT_RETURN
      )
        actionString = t('vat-report.included-this-submission');
      if (
        ExceptionsActionType[exceptionActionType] === ExceptionsActionType.DEFER
      )
        actionString = t('vat-report.deferred-next-submission');
      const message: MessageData = {
        title: t('vat-report.action-done-title'),
        description: t(
          'vat-report.selected-exceptions-marked-save-changes',
          { action: actionString }
        ),
        type: MessageStates.SUCCESS,
        duration: 5,
      };
      dispatch(turnMessageOn(message));
    }
    if (vatRunData) {
      if (vatRunData.status)
        DataLogStatus[vatRunData.status] !==
          DataLogStatus.EXCEPTIONS_MODIFIED &&
          setOriginalStatus(DataLogStatus[vatRunData.status]);
      dispatch(changeStatusAfterUpdate(DataLogStatus.EXCEPTIONS_MODIFIED));
    }
  };

  const saveExceptionAction = () => {
    const uuid = uuidv4().replaceAll('-', '');
    const exceptionsWrapper = [...handledExceptions];
    exceptionsWrapper.forEach((ew) => {
      if (ew.adjustmentPeriodId === 'OOS') ew.adjustmentPeriodId = null;
    });
    vatRunData &&
      dispatch(
        exceptionsActionRequest(
          vatRunData?.dataLogId,
          vatRunData?.mappingId,
          exceptionsWrapper,
          uuid,
          allExceptionsDetails,
          reportId
        )
      );
    setReportId(uuid);
    setHandledExceptions([]);
    setUpdatingFromPage(true);
    dispatch(changeStatusAfterUpdate(orginalStatus));
    setAllExceptionsDetails(null);
  };

  const missingTaxErrorMessage = (missingTax: string[]) => {
    return (
      <span>
        {t('vat-report.invalid-missing-vat-numbers', {
          contacts: missingTax.join(', '),
        })}
        <br />
        <br />
        {t('vat-report.amend-vat-numbers')}
      </span>
    );
  };

  const zeroAmountErrorMessage = (contacts: string[]) => {
    return (
      <span>
        {t('vat-report.zero-amount-rows', {
          contacts: contacts.join(', '),
        })}
        <br />
        <br />
        {t('vat-report.amend-zero-amounts')}
      </span>
    );
  };

  const onDownloadMaltaRecapCsvClick = (contacts: DataContact[]) => {
    const result = buildMaltaRecapPipeFile(contacts);

    if (!result.success) {
      let errorMessage: React.ReactNode = null;
      if (result.invalidVatContacts.length > 0) {
        errorMessage = missingTaxErrorMessage(result.invalidVatContacts);
      } else if (result.zeroAmountContacts.length > 0) {
        errorMessage = zeroAmountErrorMessage(result.zeroAmountContacts);
      }

      if (errorMessage) {
        const message: MessageData = {
          title: t('vat-report.file-cannot-be-created-title'),
          description: errorMessage,
          type: MessageStates.ERROR,
        };
        dispatch(turnMessageOn(message));
      }
      return;
    }

    if (result.content) {
      downloadTextFile(
        result.content,
        `${vatRunData?.connectionName}-${vatRunData?.taxReportTypeName}.txt`
      );
    }
  };

  const onDownloadCSVClick = () => {
    if (vatReportData?.contacts && vatReportData.contacts.length > 0) {
      onDownloadMaltaRecapCsvClick(vatReportData.contacts);
    }
  };

  const onDownloadXMLClick = () => {
    let xml: any = null;
    let hasErrors = false;
    let errorMessage: any = '';
    const currentOrganisation = mappedOrganisations?.find(
      (mo) => mo.connectionId === vatRunData?.tokenId
    );
    const onDownloadMaltaRecapXMLClick = (contacts: DataContact[]) => {
      const minValue = 0.0;
      const maxValue = 99999999999.99;
      const xml = builder
        .create('erecap')
        .att('xmlns:xsi', 'http://www.w3.org/2001/XMLSchema-instance')
        .att('xsi:noNamespaceSchemaLocation', 'erecap.xsd');
      const errorContacts: string[] = [];
      contacts.forEach((contact) => {
        if (
          !contact.taxNumber ||
          (contact.viesStatus &&
            ViesStatus[contact.viesStatus] !== ViesStatus.VALID)
        ) {
          if (errorContacts.findIndex((ec) => ec === contact.name) === -1)
            errorContacts.push(contact.name);
          hasErrors = true;
          errorMessage = missingTaxErrorMessage(errorContacts);
        }
        const totalOfGoods =
          contact.goodsSum >= minValue && contact.goodsSum <= maxValue
            ? contact.goodsSum.toFixed(2)
            : minValue;
        const totalOfServices =
          contact.servicesSum >= minValue && contact.servicesSum <= maxValue
            ? contact.servicesSum.toFixed(2)
            : minValue;
        xml
          .ele('application')
          .ele('triangularTrade', contact.isTriangularTrade ? '1' : '0')
          .up()
          .ele('customervatno', contact.taxNumber?.substring(0, 14))
          .up()
          .ele('customername', contact.name?.substring(0, 60))
          .up()
          .ele('totalgoods', totalOfGoods)
          .up()
          .ele('totalservices', totalOfServices)
          .up();
      });
      xml.end({ pretty: true });

      if (!hasErrors) {
        //@ts-ignore
        const data = new Blob([xml], { type: 'xml' }); //@ts-ignore
        const csvURL = window.URL.createObjectURL(data);
        const tempLink = document.createElement('a');
        tempLink.href = csvURL;
        tempLink.setAttribute(
          'download',
          `${vatRunData?.connectionName}-${vatRunData?.taxReportTypeName}.xml`
        );
        tempLink.click();
      }
    };

    const getReturnFreq = () => {
      let freq = '';
      switch (retFreq) {
        case ReturnFrequency.BI_YEARLY:
          freq = '3';
          break;

        default:
          freq = '1';
          break;
      }

      return freq;
    };

    const getCurrency = (currency) => {
      let curr = '';
      switch (currency) {
        case 'EUR':
          curr = 'E';
          break;

        default:
          curr = 'E';
          break;
      }

      return curr;
    };

    if (vatRunData?.taxReportCountry === TaxReportCountry.IRELAND_RECAP) {
      const retFrequency = currentOrganisation?.returns.find(
        (r) => r.mappingId === vatRunData?.mappingId
      )?.returnFrequency;
      if (
        retFrequency &&
        ReturnFrequency[retFrequency] !== ReturnFrequency.YEARLY &&
        ReturnFrequency[retFrequency] !== ReturnFrequency.MONTHLY &&
        ReturnFrequency[retFrequency] !== ReturnFrequency.QUARTERLY
      ) {
        hasErrors = true;
        errorMessage = (
          <span>
            {t('vat-report.unsupported-frequency', {
              frequency: getReturnFrequencyLabel(retFrequency, t),
            })}
            <br />
            <br />
            {t('vat-report.change-frequency')}
          </span>
        );
      }

      const returnType = retFrequency
        ? ReturnFrequency[retFrequency] === ReturnFrequency.YEARLY
          ? 'Annual-A1'
          : ReturnFrequency[retFrequency]
        : '';
      xml = builder
        .create('ViesFile', { encoding: 'UTF-8', standalone: false })
        .att('formversion', '1')
        .att('language', 'E')
        .att('periodstart', moment(vatRunData?.fromDate).format('DD/MM/YYYY'))
        .att('periodend', moment(vatRunData?.toDate).format('DD/MM/YYYY'))
        .ele('HeaderDetails')
        .ele('Header')
        .att('nilreturn', false)
        .att(
          'period',
          `${moment(vatRunData?.fromDate).format('DD MMM YY')} - ${moment(
            vatRunData?.toDate
          ).format('DD MMM YY')}`
        )
        .att('returntype', returnType)
        .att('returnfrequency', getReturnFreq())
        .att('thirdparty', false)
        .att('declarnumber', currentOrganisation?.vatNumber)
        .up()
        .up();

      const errorContacts: string[] = [];
      vatReportData?.contacts?.forEach((contact) => {
        if (
          !contact.taxNumber ||
          (contact.viesStatus &&
            ViesStatus[contact.viesStatus] !== ViesStatus.VALID)
        ) {
          if (errorContacts.findIndex((ec) => ec === contact.name) === -1)
            errorContacts.push(contact.name);
          hasErrors = true;
          errorMessage = errorMessage = missingTaxErrorMessage(errorContacts);
        }

        xml
          .ele('LineItemDetails')
          .ele('Details')
          .att('custnumber', contact.taxNumber)
          .att('triang', contact.isTriangularTrade)
          .att(
            'valsupplies',
            (contact.goodsSum + contact.servicesSum).toFixed(0)
          )
          .att('services', !!contact.servicesSum)
          .att('goods', !!contact.goodsSum);
      });
      xml.end();
    }

    if (vatRunData?.taxReportCountry === TaxReportCountry.IRELAND_STANDARD) {
      const sales: any = vatReportData?.subsections?.find(
        (subsec) => subsec.code == 'T1'
      )?.net;
      const purchases: any = vatReportData?.subsections?.find(
        (subsec) => subsec.code == 'T2'
      )?.net;
      const goodsTo: any = vatReportData?.subsections?.find(
        (subsec) => subsec.code == 'E1'
      )?.net;
      const goodsFrom: any = vatReportData?.subsections?.find(
        (subsec) => subsec.code == 'E2'
      )?.net;
      const servicesTo: any = vatReportData?.subsections?.find(
        (subsec) => subsec.code == 'ES1'
      )?.net;
      const servicesFrom: any = vatReportData?.subsections?.find(
        (subsec) => subsec.code == 'ES2'
      )?.net;

      xml = builder
        .create('VAT3', { encoding: 'UTF-8', standalone: 'yes' })
        .att('currency', getCurrency(vatRunData?.currency))
        .att('name', vatRunData?.connectionName.substring(0, 30))
        .att('regnum', currentOrganisation?.vatNumber)
        .att('startdate', moment(vatRunData?.fromDate).format('DD/MM/YYYY'))
        .att('enddate', moment(vatRunData?.toDate).format('DD/MM/YYYY'))
        .att('sales', parseInt(sales))
        .att('purchs', parseInt(purchases))
        .att('goodsto', parseInt(goodsTo))
        .att('goodsfrom', parseInt(goodsFrom))
        .att('servicesto', parseInt(servicesTo))
        .att('servicesfrom', parseInt(servicesFrom))
        .att('type', '0') //vatRunData.taxType
        .att('filefreq', getReturnFreq())
        .att('formversion', '1')
        .att('language', 'E')
        .end({ pretty: true });
    }
    if (vatRunData?.taxReportCountry === TaxReportCountry.MALTA) {
      vatReportData?.contacts &&
        vatReportData.contacts.length > 0 &&
        onDownloadMaltaRecapXMLClick(vatReportData?.contacts);
    } else if (!hasErrors) {
      //@ts-ignore
      const xmlData = new Blob([xml], { type: 'xml' }); //@ts-ignore
      const csvURL = window.URL.createObjectURL(xmlData);
      const tempLink = document.createElement('a');
      tempLink.href = csvURL;
      tempLink.setAttribute(
        'download',
        `${vatRunData?.connectionName}-${vatRunData?.taxReportTypeName}.xml`
      );
      tempLink.click();
    }
    if (hasErrors) {
      const message: MessageData = {
        title: t('vat-report.file-cannot-be-created-title'),
        description: errorMessage,
        type: MessageStates.ERROR,
      };
      dispatch(turnMessageOn(message));
      return;
    }
  };

  const redirectPage = (url: string) => {
    navigate(url);
  };

  const submitToHmrc = () => {
    const dataLogIdToSubmit = vatRunData?.dataLogId;
    const mappingIdToSubmit = vatRunData?.mappingId;
    if (!dataLogIdToSubmit || !mappingIdToSubmit) {
      dispatch(
        turnMessageOn({
          title: t('common.attention'),
          description: t('vat-report.missing-hmrc-identifiers'),
          type: MessageStates.WARNING,
        })
      );
      return;
    }

    const boxRows = vatReportData?.subsections?.length
      ? vatReportData.subsections
      : vatReportData?.adjustedSubsections?.length
        ? vatReportData.adjustedSubsections
        : vatReportData?.summary || [];

    const matchesUkBox = (label: string | undefined | null, boxNo: number) => {
      if (!label) return false;
      const normalized = label
        .trim()
        .toLowerCase()
        .replace(/^box\s+/, '')
        .replace(/\s+/g, '');
      return (
        normalized === String(boxNo) ||
        normalized.match(/^(\d+)/)?.[1] === String(boxNo)
      );
    };

    const parseBoxValue = (
      rawValue: unknown
    ): number | undefined => {
      if (typeof rawValue === 'number' && !Number.isNaN(rawValue)) {
        return rawValue;
      }
      if (rawValue == null) {
        return undefined;
      }
      if (typeof rawValue === 'string') {
        const normalized = rawValue.replace(/,/g, '').trim();
        if (normalized === '') return undefined;
        const parsed = parseLocaleNumber(normalized);
        return Number.isNaN(parsed) ? undefined : parsed;
      }
      return undefined;
    };

    const getUkBoxValueFromDom = (boxNo: number): number | undefined => {
      const domSpan = document.getElementById(`subsection${boxNo}-value-span`);
      if (!domSpan) return undefined;
      const domRaw = domSpan.getAttribute('data-raw-value');
      const domText = (domSpan.textContent ?? '').trim();
      const raw =
        domRaw != null && domRaw !== '' ? domRaw : domText;
      if (raw === '') return undefined;
      return parseBoxValue(raw);
    };

    const getBoxNet = (boxNo: number): number | undefined => {
      const row = boxRows.find((item) => {
        const subsection = item as TaxReportSubsectionDataResponse;
        const summary = item as BoxSummary;
        return (
          matchesUkBox(subsection.code, boxNo) ||
          matchesUkBox(summary.boxName, boxNo) ||
          matchesUkBox(subsection.cellName, boxNo)
        );
      });

      if (row) {
        const rawValue = row.net ?? (row as BoxSummary & { boxValue?: unknown }).boxValue;
        const parsed = parseBoxValue(rawValue);
        if (parsed !== undefined) {
          return parsed;
        }
        // Row exists but value is null/empty — treat as zero (valid HMRC value).
        return 0;
      }

      return getUkBoxValueFromDom(boxNo);
    };

    const periodKey =
      (vatRunData.hmrcPeriodKey || '').trim() ||
      vatRunData.obligation
        ?.map((obligation) => obligation?.periodKey?.trim())
        .find(
          (obligationPeriodKey) =>
            obligationPeriodKey && obligationPeriodKey.toLowerCase() !== 'none'
        ) ||
      '';
    const box1 = getBoxNet(1);
    const box2 = getBoxNet(2);
    const box3 = getBoxNet(3);
    const box4 = getBoxNet(4);
    const rawBox5 = getBoxNet(5);
    const box5 = rawBox5 === undefined ? undefined : Math.abs(rawBox5);
    const box6 = getBoxNet(6);
    const box7 = getBoxNet(7);
    const box8 = getBoxNet(8);
    const box9 = getBoxNet(9);

    const missingFields: string[] = [];
    if (!periodKey) missingFields.push('periodKey');
    if (box1 === undefined) missingFields.push('Box 1 (vatDueSales)');
    if (box2 === undefined) missingFields.push('Box 2 (vatDueAcquisitions)');
    if (box3 === undefined) missingFields.push('Box 3 (totalVatDue)');
    if (box4 === undefined)
      missingFields.push('Box 4 (vatReclaimedCurrPeriod)');
    if (box5 === undefined) missingFields.push('Box 5 (netVatDue)');
    if (box6 === undefined) missingFields.push('Box 6 (totalValueSalesExVAT)');
    if (box7 === undefined)
      missingFields.push('Box 7 (totalValuePurchasesExVAT)');
    if (box8 === undefined)
      missingFields.push('Box 8 (totalValueGoodsSuppliedExVAT)');
    if (box9 === undefined)
      missingFields.push('Box 9 (totalAcquisitionsExVAT)');

    if (missingFields.length > 0) {
      dispatch(
        turnMessageOn({
          title: t('common.attention'),
          description: `Cannot submit to HMRC. Missing required fields: ${missingFields.join(
            ', '
          )}`,
          type: MessageStates.WARNING,
        })
      );
      return;
    }

    const submissionBox1 = box1 ?? 0;
    const submissionBox2 = box2 ?? 0;
    const submissionBox3 = box3 ?? 0;
    const submissionBox4 = box4 ?? 0;
    const submissionBox5 = box5 ?? 0;
    const submissionBox6 = box6 ?? 0;
    const submissionBox7 = box7 ?? 0;
    const submissionBox8 = box8 ?? 0;
    const submissionBox9 = box9 ?? 0;

    const hmrcSubmissionRequest: HmrcSubmissionRequest = {
      periodKey,
      vatDueSales: submissionBox1,
      vatDueAcquisitions: submissionBox2,
      totalVatDue: submissionBox3,
      vatReclaimedCurrPeriod: submissionBox4,
      netVatDue: submissionBox5,
      totalValueSalesExVAT: submissionBox6,
      totalValuePurchasesExVAT: submissionBox7,
      totalValueGoodsSuppliedExVAT: submissionBox8,
      totalAcquisitionsExVAT: submissionBox9,
      finalised: true,
    };

    dispatch(
      submitHmrcReturnRequest(
        dataLogIdToSubmit,
        mappingIdToSubmit,
        hmrcSubmissionRequest
      )
    );
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>{`${t('returns.mapping.tax-return')} | ${process.env.PROJECT_NAME}`}</title>
        <meta name="description" content={t('users.page-meta-description')} />
      </Helmet>

      {!vatRunData && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <Spin
            spinning={
              prepLoading ||
              !!tifdLoading ||
              (!!dataLogId && !vatRunData && !tifdError)
            }
            size="large"
          />
        </div>
      )}

      {vatRunData && currentUser && (
        <VatReport
          loading={
            mappedOrganisations?.find(
              (mo) => mo.connectionId === vatRunData.tokenId
            )?.connectedProperly
              ? loading || prepLoading
              : false
          }
          attachmentLoading={attachmentLoading}
          success={success || !!tifdReport}
          returnForm={
            tifdReport ? (
              <TifdReturnForm
                report={tifdReport}
                context={tifdContext}
                workbookLoading={tifdWorkbookLoading}
                onFillWorkbook={(file) => {
                  if (!tifdReport.id || !tifdContext) return;
                  dispatch(
                    fillTifdWorkbookRequest({
                      applicationConnectionId:
                        tifdContext.applicationConnectionId,
                      reportId: tifdReport.id,
                      file,
                      periodStart: tifdReport.periodStart,
                      periodEnd: tifdReport.periodEnd,
                    })
                  );
                }}
              />
            ) : undefined
          }
          vatRunData={vatRunData}
          vatReportData={vatReportData}
          generatedDate={generatedDate}
          reloadReportData={reloadReportData}
          currentUser={currentUser}
          loadingPercent={percent}
          resetValues={resetValues}
          resetFinished={resetFinished}
          pages={pages}
          saveReturn={saveReturn}
          revertData={revertData}
          cancelReturn={cancelWarningWindow}
          setAwaitingApproval={setAsAwaitingApproval}
          setInternallyApproved={setInternallyApproved}
          updateActivityRole={updateActivityRole}
          sendForClientApproval={sendForClientApprovalWindow}
          setClientApproved={setClientApproved}
          setNotApproved={setNotApprovedWindow}
          cancelNotSaved={cancelNotSaved}
          activityLogs={activityLogs}
          updateNote={updateNote}
          cannotSave={cannotSave}
          menuSize={menuSize}
          currentOrganisation={mappedOrganisations?.find(
            (mo) => mo.connectionId === vatRunData.tokenId
          )}
          mappingSections={selectedMapping?.sections}
          returnTypeId={returnTypeId}
          returnAndMappingOnly
          onReconnect={onReconnect}
          excelFileData={excelFileData}
          loackWarningWindow={loackWarningWindow}
          openAdjustmentPanal={openAdjustmentPanal}
          handlePreferences={handlePreferences}
          addExceptionAction={addExceptionAction}
          noDataFound={noDataFound}
          subscriptionPlanTypes={subscriptionPlanTypes}
          onDownloadXMLClick={onDownloadXMLClick}
          onDownloadCSVClick={onDownloadCSVClick}
          redirectPage={redirectPage}
          reportId={reportId}
          loadCustomersAndSuppliers={(connectionId: string) => {
            // TODO: Implement loadCustomersAndSuppliers functionality
            console.log(
              'Load customers and suppliers for connection:',
              connectionId
            );
          }}
          activateVatChecker={(
            connectionId: string,
            orgName: string,
            uuid: string
          ) => {
            // TODO: Implement activateVatChecker functionality
            console.log('Activate VAT checker for:', {
              connectionId,
              orgName,
              uuid,
            });
          }}
          currentSubscription={currentSubscription}
          backendError={backendError}
          hasAdvancedUserRights={hasAdvancedUserRights}
          vatReturnLogs={selectedVatReturnLogs}
          onCompareReturn={loadComparedReturn}
          comparedReportData={comparedReportData}
          clearComparison={clearComparison}
          journalType={journalType}
          setJournalType={handleSetJournalType}
        />
      )}
    </React.Fragment>
  );
};
